import { useEffect, useState } from "react";
import { LuCopyPlus } from "react-icons/lu";
import { TbFence } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import { repositorioFazenda } from "../../sync/repositories/fazendas";
import { repositorioPasto } from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Cadastro de Pastos",
  descricao:
    "Pastos são as áreas de pastagem da fazenda usadas para alojar lotes de animais. Esse cadastro registra área, tipo de forragem e produtividade de cada pasto, e serve de base para os cadastros de Lotes e para as Rotações de Pasto.",
  secoes: [
    {
      titulo: "Dados do Pasto",
      campos: [
        {
          nome: "Fazenda",
          obrigatorio: true,
          explicacao: "A qual fazenda este pasto pertence. Se a fazenda ainda não existe, use o botão '+' ao lado do campo para cadastrá-la sem sair desta tela.",
        },
        {
          nome: "Nome do Pasto",
          obrigatorio: true,
          explicacao: "Um nome curto que identifique o pasto (ex.: 'Pasto Norte', 'Várzea'). É usado depois para escolher o pasto em Lotes e Rotações.",
        },
        {
          nome: "Área (hectares)",
          obrigatorio: true,
          explicacao: "Tamanho da área do pasto em hectares.",
        },
        {
          nome: "Tipo de Forragem",
          obrigatorio: true,
          explicacao: "Espécie de capim/forragem plantada no pasto (ex.: Brachiaria, Panicum, Tifton).",
        },
        {
          nome: "Produtividade",
          obrigatorio: true,
          explicacao: "Produção estimada de matéria seca por hectare (kg/ha), usada como referência de capacidade de suporte do pasto.",
        },
        {
          nome: "Observações",
          obrigatorio: false,
          explicacao: "Informações extras sobre o pasto, como topografia, irrigação ou histórico de uso.",
        },
      ],
    },
  ],
  dicas: [
    "Pastos funcionam normalmente offline — cadastro, edição e exclusão sincronizam depois, quando a internet voltar.",
    "Antes de excluir um pasto, verifique se ele não está vinculado a lotes ou rotações — registros vinculados podem impedir a exclusão.",
  ],
};

export default function GestaoPastos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [pastos, setPastos] = useState([]);
  const [pastosFiltrados, setPastosFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [fazendas, setFazendas] = useState([]);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    fazenda: "",
    nome: "",
    area_hectares: "",
    tipo_forragem: "",
    produtividade_kg_ha: "",
    observacoes: "",
  });

  // Carregar fazendas
  const carregarFazendas = async () => {
    try {
      const response = await repositorioFazenda.listar();
      setFazendas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
    }
  };

  // Carregar pastos
  const carregarPastos = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioPasto.listar();
      const dados = response.data.results || response.data;
      setPastos(dados);
      setPastosFiltrados(dados);
    } catch (error) {
      console.error("Erro ao carregar pastos:", error);
      alert("Erro ao carregar lista de pastos");
    } finally {
      setCarregandoLista(false);
    }
  };

  useEffect(() => {
    carregarPastos();
    carregarFazendas();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.fazenda) {
      alert("Selecione uma fazenda.");
      return false;
    }
    if (!formData.nome.trim()) {
      alert("Informe o nome do pasto.");
      return false;
    }
    if (!formData.area_hectares || Number(formData.area_hectares) <= 0) {
      alert("Informe uma área válida.");
      return false;
    }
    if (!formData.tipo_forragem.trim()) {
      alert("Informe o tipo de forragem.");
      return false;
    }
    if (!formData.produtividade_kg_ha || Number(formData.produtividade_kg_ha) <= 0) {
      alert("Informe a produtividade válida.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      fazenda: formData.fazenda,
      nome: formData.nome,
      area_hectares: Number(formData.area_hectares),
      tipo_forragem: formData.tipo_forragem,
      produtividade_kg_ha: Number(formData.produtividade_kg_ha),
      observacoes: formData.observacoes || "",
    };

    try {
      if (editando) {
        const response = await repositorioPasto.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Pasto atualizado com sucesso!");
          handleCancel();
          carregarPastos();
        }
      } else {
        const response = await repositorioPasto.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Pasto cadastrado com sucesso!");
          handleCancel();
          carregarPastos();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (pasto) => {
    setFormData({
      fazenda: pasto.fazenda?.id || pasto.fazenda || "",
      nome: pasto.nome || "",
      area_hectares: pasto.area_hectares || "",
      tipo_forragem: pasto.tipo_forragem || "",
      produtividade_kg_ha: pasto.produtividade_kg_ha || "",
      observacoes: pasto.observacoes || "",
    });
    setEditando(true);
    setEditandoId(pasto.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoPasto = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      fazenda: "",
      nome: "",
      area_hectares: "",
      tipo_forragem: "",
      produtividade_kg_ha: "",
      observacoes: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este pasto? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioPasto.excluir(id);
      alert("Pasto excluído com sucesso!");
      carregarPastos();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir pasto. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...pastos];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(p => 
        p.nome?.toLowerCase().includes(buscaLower) ||
        p.fazenda_nome?.toLowerCase().includes(buscaLower) ||
        p.tipo_forragem?.toLowerCase().includes(buscaLower)
      );
    }
    
    setPastosFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setPastosFiltrados(pastos);
  };

  // Formatar valores
  const formatarArea = (area) => {
    if (!area && area !== 0) return "—";
    return `${Number(area).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
  };

  const formatarProdutividade = (prod) => {
    if (!prod && prod !== 0) return "—";
    return `${Number(prod).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kg/ha`;
  };

  // Truncar observações
  const truncarObservacoes = (texto, maxLength = 50) => {
    if (!texto) return "—";
    if (texto.length <= maxLength) return texto;
    return texto.substring(0, maxLength) + "...";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Pastos <TbFence size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoPasto}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Pasto
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Pasto" : "Cadastrar Novo Pasto"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Fazenda, Nome, Área */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="fazenda"
                        value={formData.fazenda}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione uma fazenda</option>
                        {fazendas.map(f => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalFazenda(true)}
                        title="Cadastrar nova fazenda"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome do Pasto *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Pasto Norte, Várzea, etc."
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Área (hectares) *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="area_hectares"
                        value={formData.area_hectares}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        step="0.01"
                        min="0.01"
                        placeholder="0,00"
                        required
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">ha</span>
                    </div>
                  </div>
                </div>

                {/* Linha 2: Tipo de Forragem e Produtividade */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Forragem *</label>
                    <input
                      type="text"
                      name="tipo_forragem"
                      value={formData.tipo_forragem}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Brachiaria, Panicum, Tifton, etc."
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Produtividade *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="produtividade_kg_ha"
                        value={formData.produtividade_kg_ha}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        step="0.01"
                        min="0.01"
                        placeholder="0"
                        required
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg/ha</span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      Produção estimada de matéria seca por hectare
                    </span>
                  </div>
                </div>

                {/* Linha 3: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    rows="4"
                    placeholder="Informações adicionais sobre o pasto (topografia, irrigação, histórico, etc.)..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome, Fazenda ou Forragem</label>
                  <input
                    type="text"
                    placeholder="Digite o nome do pasto, fazenda ou tipo de forragem..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Pastos */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {pastosFiltrados.length} pasto(s) encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazenda</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Área</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Forragem</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Produtividade</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Observações</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastosFiltrados.map((pasto) => (
                        <tr key={pasto.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{pasto.id}</td>
                          <td className="p-3 text-sm font-medium">{pasto.nome}</td>
                          <td className="p-3 text-sm">{pasto.fazenda_nome || "-"}</td>
                          <td className="p-3 text-sm">{formatarArea(pasto.area_hectares)}</td>
                          <td className="p-3 text-sm">{pasto.tipo_forragem || "-"}</td>
                          <td className="p-3 text-sm">{formatarProdutividade(pasto.produtividade_kg_ha)}</td>
                          <td className="p-3 text-sm max-w-xs">{truncarObservacoes(pasto.observacoes)}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(pasto)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(pasto.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {pastosFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="8" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhum pasto encontrado com os filtros selecionados."
                              : "Nenhum pasto cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id }));
        }}
      />
    </Layout>
  );
}